import { estPageCourante, type AdresseAffichee } from '../shell/menu-contextuel-outils.js'
import { urlExportCsvNuit, urlExportCsvSoiree } from '../admin/telechargement.js'
import { nuitDeLaSoiree, type ReponseCaisse } from './caisse-nuit-model.js'

/**
 * MENU CLIC DROIT — SOIRÉES ET NUITS DE COMPTABILITÉ (lot 2, 24/10/2026) :
 * ce que le menu propose, calculé sans React ni navigateur
 * (tests/menu-soiree.test.ts). `menu-soiree.tsx` n'y ajoute que les icônes
 * et la copie.
 *
 * Navigation, copie et les gestes de lecture des fiches. Chaque adresse est
 * celle d'un lien ou d'un bouton qui existe déjà, et l'entrée demande le
 * droit de la page où il mène :
 *
 *   · la soirée     /soirees/:id               soiree.read (liens des Soirées ; API soirees.ts)
 *   · comparer      /soirees/compare?a=:id     soiree.read (bouton « Comparer » de la fiche)
 *   · la précédente /soirees/compare?a=:id&b=:prec  soiree.read (bouton « Comparer à la
 *                   précédente » de la fiche, ComparerPrecedente.tsx ; lue au clic)
 *   · l'export      /api/v1/soirees/:id/export.csv  soiree.read (bouton « Exporter CSV » de la fiche)
 *   · la nuit       /comptabilite/nuits/:date  finance.closure.read (« Fiche de la nuit » ; App.tsx)
 *   · la clôture    urlCloture(…)              finance.closure.read (« Voir / Reprendre la clôture »)
 *   · son export    /api/v1/finance/nights/:date/export.csv  finance.closure.read (« Exporter CSV »
 *                   de la fiche de nuit ; le serveur retire les montants sans leur droit)
 *   · les ventes    /comptabilite/ventes?periode=perso&du=D&au=D   finance.revenue.read
 *
 * Aucune écriture : ni ouvrir une clôture, ni valider une nuit, ni rattacher
 * une soirée. Les deux exports sont des lectures que le serveur fabrique et
 * trace ; les adresses viennent d'`admin/telechargement.ts`, écrites une fois
 * pour les fiches et le menu. Aucun nouveau droit n'est lu ici.
 */

/* ─── les adresses, telles que les pages les écrivent ─── */

export const cheminSoiree = (id: string): string => `/soirees/${id}`
/** Le bouton « Comparer » de la fiche (SoireePage) : la soirée A préremplie, B à choisir. */
export const cheminComparer = (id: string): string => `/soirees/compare?a=${id}`
export const cheminNuit = (date: string): string => `/comptabilite/nuits/${date}`
/**
 * Ventes & produits sur la seule nuit, en période « Personnalisé » : la page
 * la compare d'elle-même à la même nuit une semaine plus tôt.
 */
export const cheminVentesNuit = (date: string): string => `/comptabilite/ventes?periode=perso&du=${date}&au=${date}`

/* ─── les droits : ceux des pages où mènent les entrées ─── */

export interface DroitsMenuSoiree {
  /** soiree.read : la fiche d'une soirée, la comparaison (aucune garde web, le serveur seul). */
  soirees: boolean
  /** finance.closure.read : la fiche de nuit et la clôture (RequirePermission d'App.tsx). */
  nuits: boolean
  /** finance.revenue.read : Ventes & produits. */
  ventes: boolean
}

/** Pendant la lecture des droits, la liste est vide : tout est refusé, rien n'est promis. */
export function droitsMenuSoiree(permissions: readonly string[]): DroitsMenuSoiree {
  return {
    soirees: permissions.includes('soiree.read'),
    nuits: permissions.includes('finance.closure.read'),
    ventes: permissions.includes('finance.revenue.read'),
  }
}

/* ─── une soirée ─── */

export interface SoireeMenu { id: string; name: string }

export interface PlanSoiree {
  /** L'adresse de la fiche : « Ouvrir », l'onglet et « Copier le lien » (`entreesLien`). */
  fiche: string
  /** Faux sans soiree.read : seule la copie reste. */
  ouvrir: boolean
  /** Masquée sur la page de comparaison : elle ne lit son adresse qu'au montage. */
  comparer: string | null
  /**
   * « Comparer à la précédente » : mêmes conditions que `comparer` ; la
   * précédente est lue au clic (agir), jamais à l'ouverture du menu.
   */
  precedente: boolean
  /** L'export CSV de la fiche (route GET du serveur), avec soiree.read. */
  exporter: string | null
  /** La fiche de nuit, seulement quand l'écran a déjà lu la caisse (la nuit tranchée par le serveur). */
  nuit: string | null
}

export function planSoiree(
  s: SoireeMenu, droits: DroitsMenuSoiree, caisse?: ReponseCaisse | null, ici?: AdresseAffichee,
): PlanSoiree {
  const comparer = cheminComparer(s.id)
  const nuit = caisse ? nuitDeLaSoiree(caisse, s.id) : null
  const comparerIci = droits.soirees && !estPageCourante(comparer, { ignorerRecherche: true, ici })
  return {
    fiche: cheminSoiree(s.id),
    ouvrir: droits.soirees,
    comparer: comparerIci ? comparer : null,
    precedente: comparerIci,
    exporter: droits.soirees ? urlExportCsvSoiree(s.id) : null,
    nuit: droits.nuits && nuit ? cheminNuit(nuit.business_date) : null,
  }
}

/* ─── une nuit de comptabilité ─── */

export interface NuitMenu {
  /** La nuit commerciale, AAAA-MM-JJ. */
  date: string
  /** Ses soirées quand l'écran les a, identifiant compris ; sans elles, pas d'entrée « Ouvrir la soirée ». */
  soirees?: Array<{ id: string; name: string; status?: string }> | null | undefined
  /**
   * Sa clôture : `null` = aucune, absente = l'écran ne la connaît pas.
   * `href` : l'adresse du lien de l'écran quand ce n'est pas `urlCloture`
   * (une révision remplacée, la clôture finalisée du chiffre d'affaires).
   */
  cloture?: { statut: string; href?: string | undefined } | null | undefined
}

/**
 * Une nuit telle que les écrans de comptabilité la reçoivent (`NightRow` de
 * `GET /api/v1/finance/nights`) : sa date, ses soirées, sa clôture courante.
 */
export function nuitDeLigne(n: {
  business_date: string
  soirees: Array<{ id: string; name: string; status: string }>
  closing: { status: string } | null
}): NuitMenu {
  return { date: n.business_date, soirees: n.soirees, cloture: n.closing ? { statut: n.closing.status } : null }
}

/**
 * Une nuit du chiffre d'affaires (`NuitCA`) : ses soirées n'y sont que des
 * noms, sans identifiant (pas d'entrée « Ouvrir la soirée ») ; sa clôture est
 * celle, finalisée, que la page lie par son identifiant (`CelluleCloture`).
 */
export function nuitDuChiffreAffaires(n: { date: string; closingId: string | null; statut: string | null }): NuitMenu {
  return {
    date: n.date,
    cloture: n.closingId !== null ? { statut: n.statut ?? 'CLOSED', href: `/comptabilite/clotures/${n.closingId}` } : null,
  }
}

export interface EntreeSoireeNuit { id: string; libelle: string; href: string }

export interface PlanNuit {
  titre: string
  fiche: string
  /** Faux sans finance.closure.read : ni la nuit ni sa clôture. */
  ouvrir: boolean
  cloture: { libelle: 'Voir la clôture' | 'Reprendre la clôture'; href: string } | null
  /** Une soirée : une entrée ; plusieurs : un sous-menu, les maintenues d'abord. */
  soiree: { id: 'soiree'; libelle: string; href: string } | { id: 'soirees'; libelle: string; sousMenu: EntreeSoireeNuit[] } | null
  /** Masquée sur Ventes & produits, qui ne lit son adresse qu'au montage. */
  ventes: string | null
  /** L'export CSV de la fiche de nuit, avec finance.closure.read : le même fichier que son bouton. */
  exporter: string | null
  /** « Samedi 24 octobre 2026 » : ce que « Copier la date » copie. */
  date: string
}

/** Les mots de la fiche de nuit (FinanceNightPage) : une clôture finalisée se voit, les autres se reprennent. */
export function libelleCloture(statut: string): 'Voir la clôture' | 'Reprendre la clôture' {
  return statut === 'CLOSED' || statut === 'SUPERSEDED' ? 'Voir la clôture' : 'Reprendre la clôture'
}

// La date d'une NUIT, pas d'un instant : lue à midi UTC, elle ne glisse jamais d'un jour.
const FMT_NUIT = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
/** « samedi 24 octobre 2026 ». */
export const nuitEnToutesLettres = (date: string): string => FMT_NUIT.format(new Date(`${date}T12:00:00Z`))
const majuscule = (t: string): string => t.charAt(0).toUpperCase() + t.slice(1)
/** Le titre du menu : « Nuit du samedi 24 octobre 2026 ». */
export const titreNuit = (date: string): string => `Nuit du ${nuitEnToutesLettres(date)}`

/** Les soirées d'une nuit, à ouvrir : les maintenues d'abord, les annulées dites comme dans le calendrier. */
export function soireesDeLaNuit(soirees: ReadonlyArray<{ id: string; name: string; status?: string }>): EntreeSoireeNuit[] {
  const annulee = (s: { status?: string }) => s.status === 'CANCELLED'
  return [...soirees.filter((s) => !annulee(s)), ...soirees.filter(annulee)].map((s) => ({
    id: `soiree-${s.id}`,
    libelle: annulee(s) ? `Annulée — ${s.name}` : s.name,
    href: cheminSoiree(s.id),
  }))
}

/**
 * `adresseCloture` : `urlCloture` de nights-common, passée par l'appelant
 * (un module avec React, que ce calcul n'importe pas).
 */
export function planNuit(
  n: NuitMenu, droits: DroitsMenuSoiree,
  adresseCloture: (date: string, soirees?: Array<{ name: string; status?: string }> | null) => string,
  ici?: AdresseAffichee,
): PlanNuit {
  const ventes = cheminVentesNuit(n.date)
  const liste = droits.soirees ? soireesDeLaNuit(n.soirees ?? []) : []
  const seule = liste.length === 1 ? (n.soirees ?? [])[0] ?? null : null
  return {
    titre: titreNuit(n.date),
    fiche: cheminNuit(n.date),
    ouvrir: droits.nuits,
    cloture: droits.nuits && n.cloture
      ? { libelle: libelleCloture(n.cloture.statut), href: n.cloture.href ?? adresseCloture(n.date, n.soirees) }
      : null,
    soiree: seule
      ? { id: 'soiree', libelle: `Ouvrir la soirée ${seule.status === 'CANCELLED' ? 'annulée ' : ''}${seule.name}`, href: cheminSoiree(seule.id) }
      : liste.length > 1 ? { id: 'soirees', libelle: 'Ouvrir une soirée', sousMenu: liste } : null,
    ventes: droits.ventes && !estPageCourante(ventes, { ignorerRecherche: true, ici }) ? ventes : null,
    exporter: droits.nuits ? urlExportCsvNuit(n.date) : null,
    date: majuscule(nuitEnToutesLettres(n.date)),
  }
}
